/* organization/Console — Organization Dashboard (Phase 1).
   Runtime-only. Consumes OrganizationRuntimeContext exclusively.
   No direct API calls. No data.state.* access. */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill, Btn } from '../shared/ui';
import { PageHead, StatCard } from '../platform/_ui';
import { statusTone, PLAN_TONES } from '../platform/platformStore';
import {
  useOrgRuntime,
  useOrganization,
  useSubscription,
  useUsage,
  useLimits,
  useOrgFeatures,
  useOrgPermissions,
  useOrgNavigation,
  useOrgNotifications,
} from './OrganizationRuntimeContext.jsx';

/* ── Constants ──────────────────────────────────────────────────────────── */

const FEATURE_CATALOG = [
  { key: 'crm',               label: 'CRM',           icon: 'Users',         color: 'var(--nx-accent)' },
  { key: 'analytics',         label: 'Analytics',     icon: 'BarChart2',     color: 'var(--nx-accent)' },
  { key: 'channels',          label: 'Channels',      icon: 'MessageSquare', color: 'var(--nx-accent)' },
  { key: 'ai',                label: 'AI Platform',   icon: 'Cpu',           color: 'var(--nx-accent)' },
  { key: 'storage.documents', label: 'Documents',     icon: 'HardDrive',     color: 'var(--nx-accent)' },
  { key: 'developer',         label: 'Developer',     icon: 'Code',          color: 'var(--nx-accent)' },
  { key: 'org.audit',         label: 'Audit Logs',    icon: 'ClipboardList', color: 'var(--nx-accent)' },
  { key: 'org.health',        label: 'Health Center', icon: 'HeartPulse',    color: 'var(--nx-accent)' },
  { key: 'org.security',      label: 'Security',      icon: 'ShieldCheck',   color: 'var(--nx-accent)' },
  { key: 'org.teams',         label: 'Teams',         icon: 'UsersRound',    color: 'var(--nx-accent)' },
  { key: 'org.branding',      label: 'Branding',      icon: 'Eye',           color: 'var(--nx-accent)' },
  { key: 'org.backup',        label: 'Backup',        icon: 'Archive',       color: 'var(--nx-accent)' },
  { key: 'org.templates',     label: 'Templates',     icon: 'Layout',        color: 'var(--nx-accent)' },
];

const ALERT_ICONS = {
  TRIAL_ENDING:           'Timer',
  SUBSCRIPTION_EXPIRING:  'CalendarClock',
  SUBSCRIPTION_SUSPENDED: 'Ban',
  CAPACITY_WARNING:       'Users',
  PAYMENT_FAILED:         'CreditCard',
};

const HEALTH_CONFIG = {
  healthy:  { color: 'var(--nx-success)', bg: 'var(--nx-success-tint)', icon: 'CheckCircle',  label: 'Healthy'         },
  warning:  { color: 'var(--nx-warning)', bg: 'var(--nx-warning-tint)', icon: 'AlertTriangle', label: 'Warning'         },
  critical: { color: 'var(--nx-danger)',  bg: 'var(--nx-danger-tint)',  icon: 'XCircle',      label: 'Needs Attention' },
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

const safePct = (used, max) => (max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0);

const barColor = (p) => {
  if (p >= 90) return 'var(--nx-danger, #DC2626)';
  if (p >= 70) return 'var(--nx-warning, #B5740E)';
  return 'var(--nx-success, #0E8C5A)';
};

function statColor(p) {
  if (p >= 90) return 'red';
  if (p >= 70) return 'amber';
  return 'green';
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDays = (n) => {
  if (n == null) return null;
  if (n <= 0)   return 'Today';
  if (n === 1)  return 'Tomorrow';
  return `${n} days`;
};

const computeOrgAge = (createdAt) => {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
  if (days < 30)  return `${days}d old`;
  if (days < 365) return `${Math.floor(days / 30)}mo old`;
  return `${Math.floor(days / 365)}yr old`;
};

const fmtCycle = (c) => (c ? c.charAt(0) + c.slice(1).toLowerCase() : '—');

function featureEnabled(resolvedFeatures, keyPrefix) {
  return resolvedFeatures.some(k => k === keyPrefix || k.startsWith(keyPrefix + '.'));
}

function computeHealth(notifications, orgStatus) {
  if (orgStatus === 'SUSPENDED') return 'critical';
  if (orgStatus === 'ARCHIVED')  return 'warning';
  const alerts = notifications?.alerts ?? [];
  if (alerts.some(a => a.severity === 'CRITICAL')) return 'critical';
  if (alerts.some(a => a.severity === 'WARNING'))  return 'warning';
  return 'healthy';
}

/* ── Skeleton ───────────────────────────────────────────────────────────── */

function DashSkeleton() {
  return (
    <div className="nx-cc-page">
      <div className="nx-cc-skeleton" style={{ height: 72, borderRadius: 8 }} />
      <div className="nx-cc-stat-grid nx-kpi-4">
        {[1,2,3,4].map(i => <div key={i} className="nx-cc-skeleton" style={{ height: 88, borderRadius: 8 }} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
        {[1,2,3,4,5].map(i => <div key={i} className="nx-cc-skeleton" style={{ height: 96, borderRadius: 8 }} />)}
      </div>
      <div className="nx-cc-skeleton" style={{ height: 200, borderRadius: 8 }} />
    </div>
  );
}

/* ── HealthPanel ────────────────────────────────────────────────────────── */

const UNHEALTHY_SUB = new Set(['SUSPENDED', 'EXPIRED', 'CANCELLED']);

function HealthPanel({ level, notifications, subscription, limits, usage }) {
  const cfg     = HEALTH_CONFIG[level] ?? HEALTH_CONFIG.healthy;
  const alerts  = notifications?.alerts ?? [];
  const subStatus     = subscription?.status ?? '';
  const checks = [
    { label: 'Subscription', ok: UNHEALTHY_SUB.has(subStatus) === false, detail: subStatus || 'None' },
    { label: 'User Seats',   ok: safePct(usage.users      ?? 0, limits.maxUsers      ?? 0) < 90, detail: `${usage.users ?? 0} / ${limits.maxUsers ?? 0}` },
    { label: 'Workspaces',   ok: safePct(usage.workspaces ?? 0, limits.maxWorkspaces ?? 0) < 90, detail: `${usage.workspaces ?? 0} / ${limits.maxWorkspaces ?? 0}` },
    { label: 'Storage',      ok: safePct(usage.storageGb  ?? 0, limits.maxStorageGb  ?? 0) < 90, detail: `${usage.storageGb ?? 0} / ${limits.maxStorageGb ?? 0} GB` },
  ];

  return (
    <div className="nx-cc-chart-card" style={{ gap: 12 }}>
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Organization Health</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '3px 10px', borderRadius: 20 }}>
          <NexusIcon name={cfg.icon} size={13} />
          {cfg.label}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {checks.map(c => (
          <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--nx-border)' }}>
            <NexusIcon name={c.ok ? 'CheckCircle' : 'AlertCircle'} size={13} style={{ color: c.ok ? '#0E8C5A' : '#B5740E', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{c.label}</span>
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{c.detail}</span>
          </div>
        ))}
      </div>

      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {alerts.map(a => (
            <div key={a.type} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 10px', borderRadius: 6, background: a.severity === 'CRITICAL' ? 'rgba(220,38,38,0.07)' : 'rgba(181,116,14,0.07)' }}>
              <NexusIcon name={ALERT_ICONS[a.type] ?? 'Bell'} size={13} style={{ color: a.severity === 'CRITICAL' ? '#DC2626' : '#B5740E', marginTop: 1, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--nx-text)', lineHeight: 1.4 }}>{a.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── RuntimeStatusPanel ─────────────────────────────────────────────────── */

function RuntimeStatusPanel({ org, subscription, usage, limits }) {
  const checks = [
    { label: 'Runtime API',   ok: true,                                                          detail: 'Connected',                                              icon: 'Wifi'       },
    { label: 'Organization',  ok: org?.status === 'ACTIVE',                                      detail: org?.status ?? '—',                                       icon: 'Building2'  },
    { label: 'Subscription',  ok: !UNHEALTHY_SUB.has(subscription?.status ?? ''),                detail: subscription?.status ?? 'None',                           icon: 'CreditCard' },
    { label: 'Seat Capacity', ok: safePct(usage.users ?? 0, limits.maxUsers ?? 0) < 90,          detail: `${usage.users ?? 0} / ${limits.maxUsers ?? 0}`,           icon: 'Users'      },
    { label: 'Storage',       ok: safePct(usage.storageGb ?? 0, limits.maxStorageGb ?? 0) < 90,  detail: `${usage.storageGb ?? 0} / ${limits.maxStorageGb ?? 0} GB`, icon: 'HardDrive'  },
  ];
  const allOk = checks.every(c => c.ok);

  return (
    <div className="nx-cc-chart-card" style={{ gap: 12 }}>
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>System Status</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: allOk ? '#0E8C5A' : '#B5740E', background: allOk ? 'rgba(14,140,90,0.08)' : 'rgba(181,116,14,0.08)', padding: '3px 10px', borderRadius: 20 }}>
          <NexusIcon name={allOk ? 'CheckCircle' : 'AlertTriangle'} size={13} />
          {allOk ? 'All Systems OK' : 'Issues Detected'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {checks.map(c => (
          <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--nx-border)' }}>
            <NexusIcon name={c.ok ? 'CheckCircle' : 'AlertCircle'} size={13} style={{ color: c.ok ? '#0E8C5A' : '#B5740E', flexShrink: 0 }} />
            <NexusIcon name={c.icon} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{c.label}</span>
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{c.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── SubscriptionSummary ────────────────────────────────────────────────── */

function SubscriptionSummary({ subscription, limits, usage, orgId, navigate }) {
  if (!subscription) {
    return (
      <div className="nx-cc-chart-card" style={{ gap: 12 }}>
        <div className="nx-cc-chart-head"><span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Subscription</span></div>
        <div className="nx-empty" style={{ padding: '20px 0' }}><NexusIcon name="CreditCard" size={18} /><span>No active subscription.</span></div>
      </div>
    );
  }

  const seatPct    = safePct(usage.users ?? 0, limits.maxUsers ?? 0);
  const storagePct = safePct(usage.storageGb ?? 0, limits.maxStorageGb ?? 0);
  const wsPct      = safePct(usage.workspaces ?? 0, limits.maxWorkspaces ?? 0);
  const needsUpgrade = seatPct >= 80 || storagePct >= 80 || wsPct >= 80;

  const rows = [
    { label: 'Plan',          value: subscription.planName ?? '—',                         tone: PLAN_TONES[subscription.planTier] ?? 'gray', isChip: true },
    { label: 'Billing',       value: fmtCycle(subscription.billingCycle) },
    { label: 'Renewal',       value: fmtDate(subscription.renewalAt) },
    { label: 'Seats',         value: `${usage.users ?? 0} / ${limits.maxUsers ?? 0}`,      pct: seatPct },
    { label: 'Storage',       value: `${usage.storageGb ?? 0} / ${limits.maxStorageGb ?? 0} GB`, pct: storagePct },
    { label: 'Workspaces',    value: `${usage.workspaces ?? 0} / ${limits.maxWorkspaces ?? 0}`,  pct: wsPct },
  ];

  return (
    <div className="nx-cc-chart-card" style={{ gap: 12 }}>
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Subscription</span>
        <Pill tone={statusTone(subscription.status)}>{subscription.status}</Pill>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {rows.map(r => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--nx-border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', width: 80, flexShrink: 0 }}>{r.label}</span>
            {r.isChip
              ? <Pill tone={r.tone}>{r.value}</Pill>
              : <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{r.value}</span>
            }
            {r.pct != null && (
              <span style={{ fontSize: 11, color: barColor(r.pct), fontWeight: 700 }}>{r.pct}%</span>
            )}
          </div>
        ))}
      </div>

      {subscription.isInTrial && subscription.trialEndsAt && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(181,116,14,0.08)', fontSize: 12, color: '#B5740E', fontWeight: 600 }}>
          <NexusIcon name="Timer" size={13} style={{ marginRight: 6 }} />
          Trial ends {fmtDate(subscription.trialEndsAt)}
          {subscription.daysUntilRenewal != null && ` · ${fmtDays(subscription.daysUntilRenewal)}`}
        </div>
      )}

      {needsUpgrade && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'color-mix(in srgb, var(--nx-accent, #125AB0) 7%, transparent)', fontSize: 12, color: 'var(--nx-accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="TrendingUp" size={13} />
          <span>Usage is approaching limits — consider upgrading your plan.</span>
          <button onClick={() => navigate(`/organization/${orgId}/billing`)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--nx-accent)', cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}>
            Upgrade →
          </button>
        </div>
      )}
    </div>
  );
}

/* ── FeatureGrid ────────────────────────────────────────────────────────── */

function FeatureGrid({ resolvedFeatures, orgId, navigate }) {
  const enabled     = FEATURE_CATALOG.filter(f =>  featureEnabled(resolvedFeatures, f.key));
  const unavailable = FEATURE_CATALOG.filter(f => !featureEnabled(resolvedFeatures, f.key));

  return (
    <div className="nx-cc-chart-card">
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Features</span>
        <button
          onClick={() => navigate(`/organization/${orgId}/licenses`)}
          style={{ background: 'none', border: 'none', color: 'var(--nx-accent)', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
        >
          Licenses <NexusIcon name="ExternalLink" size={11} />
        </button>
      </div>

      {enabled.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
            Enabled ({enabled.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: unavailable.length > 0 ? 16 : 0 }}>
            {enabled.map(f => (
              <div key={f.key} style={{ '--_fc': f.color, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 20, border: '1.5px solid color-mix(in srgb, var(--_fc) 16%, transparent)', background: 'color-mix(in srgb, var(--_fc) 6%, transparent)' }}>
                <NexusIcon name={f.icon} size={12} style={{ color: 'var(--_fc)', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--_fc)' }}>{f.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {unavailable.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
            Not on this plan ({unavailable.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {unavailable.map(f => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 20, border: '1.5px solid var(--nx-border)', background: 'transparent', opacity: 0.45 }}>
                <NexusIcon name={f.icon} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>{f.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {enabled.length === 0 && unavailable.length === 0 && (
        <div className="nx-empty" style={{ padding: '20px 0' }}><NexusIcon name="Package" size={18} /><span>No features configured.</span></div>
      )}
    </div>
  );
}

/* ── QuickAccess ────────────────────────────────────────────────────────── */

const NAV_ICON_COLORS = {
  workspaces: 'var(--nx-accent)', members:   'var(--nx-accent)', analytics: 'var(--nx-accent)',
  settings:   'var(--nx-accent)', storage:   'var(--nx-accent)', channels:  'var(--nx-accent)',
  audit:      'var(--nx-accent)', iam:       'var(--nx-accent)', licenses:  'var(--nx-accent)',
  branding:   'var(--nx-accent)', developer: 'var(--nx-accent)', health:    'var(--nx-danger)',
  ai:         'var(--nx-accent)', timeline:  'var(--nx-accent)',
};

function QuickAccess({ navItems, navigate }) {
  const shortcuts = navItems.filter(it => it.visible && it.key !== 'dashboard').slice(0, 8);

  if (shortcuts.length === 0) return null;

  return (
    <div className="nx-cc-chart-card">
      <div className="nx-cc-chart-head">
        <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Quick Access</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 4 }}>
        {shortcuts.map(it => {
          const color = NAV_ICON_COLORS[it.key] ?? 'var(--nx-accent)';
          return (
            <button
              key={it.key}
              type="button"
              className="nx-nav-shortcut"
              onClick={() => navigate(it.path)}
              style={{ '--_nc': color }}
            >
              <span style={{ width: 34, height: 34, borderRadius: 9, background: 'color-mix(in srgb, var(--_nc) 9%, transparent)', color: 'var(--_nc)', display: 'grid', placeItems: 'center' }}>
                <NexusIcon name={it.icon} size={16} />
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text)', textAlign: 'center', lineHeight: 1.3 }}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── OrgOverview (header strip) ─────────────────────────────────────────── */

function OrgOverview({ org, subscription, branding, orgId, navigate }) {
  const logoText = branding?.logoText ?? org.name;
  const renewal  = subscription?.daysUntilRenewal == null ? null : fmtDays(subscription.daysUntilRenewal);

  return (
    <button
      type="button"
      className="nx-cc-chart-card"
      style={{ flexDirection: 'row', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 18px', cursor: 'pointer', width: '100%', border: 'none', textAlign: 'left', background: 'none' }}
      onClick={() => navigate(`/organization/${orgId}/settings`)}
      title="Open Settings"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 200px', minWidth: 0 }}>
        {branding?.logoUrl
          ? <img src={branding.logoUrl} alt={logoText} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--nx-border)' }} />
          : <span style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--nx-accent-tint, rgba(18,90,176,0.1))', color: 'var(--nx-accent)', display: 'grid', placeItems: 'center', fontSize: 18, fontWeight: 900, flexShrink: 0 }}>
              {(logoText[0] ?? 'O').toUpperCase()}
            </span>
        }
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoText}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>
            {[org.industry, org.country].filter(Boolean).join(' · ')}
          </div>
          {(org.ownerEmail || org.createdAt) && (
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {org.ownerEmail && <span>Owner: {org.ownerEmail}</span>}
              {org.createdAt  && <span>{computeOrgAge(org.createdAt)} · {fmtDate(org.createdAt)}</span>}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Pill tone={statusTone(org.status)}>{org.status}</Pill>
        {subscription?.planName && <Pill tone={PLAN_TONES[subscription.planTier] ?? 'gray'}>{subscription.planName}</Pill>}
        {subscription?.isInTrial && <Pill tone="amber">Trial</Pill>}
        {subscription?.billingCycle && <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{fmtCycle(subscription.billingCycle)}</span>}
        {renewal && <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Renews {renewal}</span>}
        <NexusIcon name="Settings" size={13} style={{ color: 'var(--nx-text-muted)', marginLeft: 4 }} />
      </div>
    </button>
  );
}

/* ── Main Dashboard ─────────────────────────────────────────────────────── */

export default function Console() {
  const { orgId }    = useParams();
  const navigate     = useNavigate();
  const { runtime, loading, error, refreshRuntime } = useOrgRuntime();
  const org          = useOrganization();
  const subscription = useSubscription();
  const usage        = useUsage();
  const limits       = useLimits();
  const features     = useOrgFeatures();
  const permissions  = useOrgPermissions();
  const navItems     = useOrgNavigation();
  const notifications = useOrgNotifications();
  const branding     = runtime?.branding ?? null;

  if (loading) return <DashSkeleton />;

  if (error) {
    return (
      <div className="nx-cc-page">
        <div className="nx-empty" style={{ marginTop: 48 }}>
          <NexusIcon name="AlertCircle" size={24} />
          <span>Failed to load dashboard — {error}</span>
          <Btn variant="outline" size="sm" onClick={refreshRuntime}>Retry</Btn>
        </div>
      </div>
    );
  }

  if (!org) return null;

  const healthLevel  = computeHealth(notifications, org.status);

  const headActions = (
    <div style={{ display: 'flex', gap: 8 }}>
      {permissions.canManageMembers && (
        <Btn variant="outline" size="sm" icon="Users" onClick={() => navigate(`/organization/${orgId}/members`)}>Members</Btn>
      )}
      {permissions.canManageWorkspaces && (
        <Btn variant="secondary" size="sm" icon="LayoutGrid" onClick={() => navigate(`/organization/${orgId}/workspaces`)}>Workspaces</Btn>
      )}
      <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={refreshRuntime} title="Refresh dashboard" />
    </div>
  );

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Dashboard"
        sub={`${org.name} · ${org.industry || 'Organization'}`}
        actions={headActions}
      />

      {/* Section 1 — Org overview strip */}
      <OrgOverview org={org} subscription={subscription} branding={branding} orgId={orgId} navigate={navigate} />

      {/* Section 2 — KPI strip */}
      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard
          icon="Users"
          label="Members"
          value={String(usage.users ?? 0)}
          sub={limits.maxUsers > 0 ? `of ${limits.maxUsers} · ${safePct(usage.users ?? 0, limits.maxUsers)}%` : 'No seat limit'}
          color={statColor(safePct(usage.users ?? 0, limits.maxUsers ?? 0))}
          onClick={() => navigate(`/organization/${orgId}/members`)}
        />
        <StatCard
          icon="LayoutGrid"
          label="Workspaces"
          value={String(usage.workspaces ?? 0)}
          sub={limits.maxWorkspaces > 0 ? `of ${limits.maxWorkspaces} · ${safePct(usage.workspaces ?? 0, limits.maxWorkspaces)}%` : 'No limit'}
          color={statColor(safePct(usage.workspaces ?? 0, limits.maxWorkspaces ?? 0))}
          onClick={() => navigate(`/organization/${orgId}/workspaces`)}
        />
        <StatCard
          icon="HardDrive"
          label="Storage"
          value={`${usage.storageGb ?? 0} GB`}
          sub={limits.maxStorageGb > 0 ? `of ${limits.maxStorageGb} GB · ${safePct(usage.storageGb ?? 0, limits.maxStorageGb)}%` : 'No quota'}
          color={statColor(safePct(usage.storageGb ?? 0, limits.maxStorageGb ?? 0))}
          onClick={() => navigate(`/organization/${orgId}/storage`)}
        />
        <StatCard
          icon="Tag"
          label="Plan"
          value={subscription?.planName ?? org.plan ?? '—'}
          sub={subscription?.status ?? org.status ?? '—'}
          color={PLAN_TONES[subscription?.planTier] ?? 'blue'}
          onClick={() => navigate(`/organization/${orgId}/billing`)}
        />
      </div>

      {/* Sections 3 + 6 — Health | Subscription (2-col) */}
      <div className="nx-cc-grid2">
        <HealthPanel
          level={healthLevel}
          notifications={notifications}
          subscription={subscription}
          limits={limits}
          usage={usage}
        />
        <SubscriptionSummary
          subscription={subscription}
          limits={limits}
          usage={usage}
          orgId={orgId}
          navigate={navigate}
        />
      </div>

      {/* Section 4 — Features (enabled + unavailable) */}
      <FeatureGrid resolvedFeatures={features} orgId={orgId} navigate={navigate} />

      {/* Section 7 — Quick access */}
      <QuickAccess navItems={navItems} navigate={navigate} />

      {/* Section 8 — System status (runtime-derived, read-only) */}
      <RuntimeStatusPanel org={org} subscription={subscription} usage={usage} limits={limits} />

      {/* Section 5 — Notifications panel (only when alerts exist) */}
      {notifications.alerts.length > 0 && (
        <div className="nx-cc-chart-card">
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title" style={{ fontWeight: 800 }}>Alerts</span>
            <Pill tone="red">{notifications.unreadCount}</Pill>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            {notifications.alerts.map(a => {
              const isCritical = a.severity === 'CRITICAL';
              const alertColor = isCritical ? '#DC2626' : '#B5740E';
              const alertBg    = isCritical ? 'rgba(220,38,38,0.07)'  : 'rgba(181,116,14,0.07)';
              const alertBdr   = isCritical ? 'rgba(220,38,38,0.15)'  : 'rgba(181,116,14,0.15)';
              return (
                <div key={a.type} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 6, background: alertBg, border: `1px solid ${alertBdr}` }}>
                  <NexusIcon name={ALERT_ICONS[a.type] ?? 'Bell'} size={14} style={{ color: alertColor, marginTop: 1, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{a.message}</div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{a.type.replaceAll('_', ' ')}</div>
                  </div>
                  {a.actionUrl && (
                    <button onClick={() => navigate(a.actionUrl)} style={{ background: 'none', border: 'none', color: 'var(--nx-accent)', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', padding: 0 }}>
                      View →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
